import NodeRSA from 'node-rsa';
import * as apis from '@/apis/auth';
import TokenService from './tokenService';
const Buffer = require('buffer/').Buffer;
const axios = require('axios');
import createStore from '../store';
import { navLinks } from './common';
import * as actions from '@/actions/auth';

const getSignatureStr = (clientCode, authorization, body, queryParam) => {
  const bodyString = JSON.stringify(body) || '';
  return `Signature{clientCode='${clientCode}', authorization='${authorization}', body='${bodyString}', queryParam='${queryParam}'}`;
};

/**
 * sign payload with privateKey
 * @param {string} encodedPrivateKey
 * @param {*} payload
 * @return {string} signature
 */
export const doSign = (encodedPrivateKey, payload) => {
  try {
    // 1. Decode the private key with base64 then pkcs8
    const key = new NodeRSA();
    key.importKey(Buffer.from(encodedPrivateKey, 'base64'), 'pkcs8-private-der');
    const privateKey = key.exportKey();

    // 2. Sign the data with the decoded private key and sha256 then encode it with base64
    const signature = new NodeRSA(privateKey, { signingScheme: 'sha256' }).sign(payload).toString('base64');
    return signature;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

const instance = axios.create({
  transformRequest: [
    (data, headers) => {
      const encodedPrivateKey = headers.common.key;
      if (encodedPrivateKey) {
        const clientCode = headers.common['x-client-code'];
        const authToken = headers.common.authToken;
        if (authToken) {
          const payload = getSignatureStr(clientCode, authToken, data, '');
          headers.common['x-signature'] = doSign(encodedPrivateKey, payload);
          headers.common['authorization'] = authToken;
          delete headers.common['authToken'];
          delete headers.common['key'];
        }
      }
      return data;
    },
    ...axios.defaults.transformRequest
  ]
});

export const setToken = (token) => {
  instance.defaults.headers.common['authToken'] = token;
};

export const setAuthorizationToken = (token) => {
  instance.defaults.headers.common['Authorization'] = token;
};

export const removeToken = () => {
  delete instance.defaults.headers.common['authToken'];
};

export const removeApigeeToken = () => {
  delete instance.defaults.headers.common['Authorization'];
};

export const setApigeeToken = (token) => {
  instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
};

export const setxAuthToken = (token) => {
  instance.defaults.headers.common['X-Authorization'] = `${token}`;
};

export const removexAuthToken = () => {
  delete instance.defaults.headers.common['X-Authorization'];
};

export const setxPageKeyToken = (token) => {
  instance.defaults.headers.common['x-page-key'] = `${token}`;
};

export const removexPageKeyToken = () => {
  delete instance.defaults.headers.common['x-page-key'];
};

export const setXClientHeader = (token) => {
  instance.defaults.headers.common['X-CLIENT-ID'] = `${token}`;
};

export const removeXClientHeader = () => {
  delete instance.defaults.headers.common['X-CLIENT-ID'];
};

export const setPromoConfigToken = (isPromoConfigured) => {
  instance.defaults.headers.common['EMS-ESHOP2-PROMO-HDR'] = isPromoConfigured;
};

export const setDefaultHeader = (clientCode, privateKey) => {
  instance.defaults.headers.post['Content-Type'] = 'application/json;charset=UTF-8';
  instance.defaults.headers.common['x-client-code'] = clientCode;
  instance.defaults.headers.common['key'] = privateKey;
};

export const refreshTokenErrorRedirect = () => {
  window.alert('Facing issue trying to get the token, do you want to login again?');
  window.location.replace(navLinks.myEmpowersite);
};

export const checkUDFTokenExpired = () => {
  if (TokenService.getLocalExpiryAt() !== 0) {
    const tokenThreshold = 30000;
    const getCurrentTime = new Date().getTime();
    const expiryTime = new Date(TokenService.getLocalExpiryAt() - tokenThreshold).getTime();
    if (expiryTime < getCurrentTime)
      return true;
  }
  return false;
};

let isRefreshing = false;
instance.interceptors.request.use(
  async (config) => {
    if (config.url.includes('email/otp') || config.url.includes('mfa/devices')) {
      delete config.headers['x-page-key'];
    } else {
      const { store } = createStore;
        const auth = store.getState().auth;
        config.headers['x-page-key'] = auth.check;
      const promoConfigs = store.getState().promoConfigs
      config.headers['EMS-ESHOP2-PROMO-HDR'] = promoConfigs.isPromoConfigured;
    }
    if (!checkUDFTokenExpired()) return config;
    if (isRefreshing) {
      return config;
    }
    try {
      isRefreshing = true;
      let res: any = await apis.getRefreshToken();
      if (res !== null && Object.keys(res.data).length > 0) {
        let access_token = '';
        if (localStorage.getItem("udf_token")) {
          TokenService.setEmpowerTokenData(res.data);
          access_token = res.data.accessToken;
        } else {
          TokenService.setLocalTokenData(res.data);
          access_token = res.data.tokenB;
        }
        setAuthorizationToken(access_token);
        const channelTokken = await apis.fetchChannelToken();
        TokenService.setUDFChannelToken(channelTokken.data.access_token);
        setApigeeToken(channelTokken.data.access_token);
        setxAuthToken(access_token);
        const { store } = createStore;
        setxPageKeyToken(store.getState().auth.check);
        setPromoConfigToken(store.getState().promoConfigs.isPromoConfigured);
        config.headers['Authorization'] = `Bearer ${channelTokken.data.access_token}`;
        config.headers['X-Authorization'] = TokenService.getLocalAccessToken();
      } else if (res !== null && Object.keys(res.data).length === 0 && res.status === 200 && checkUDFTokenExpired()) {
        refreshTokenErrorRedirect();
      }
      isRefreshing = false;
      return config;
    } catch (error) {
      refreshTokenErrorRedirect();
      isRefreshing = false;
      return Promise.resolve(error);
    }
  },
  (error) => error
);

let refreshing_token = null;

const shouldCallRefreshToken = () => {
  return refreshing_token ? refreshing_token : (checkUDFTokenExpired() ? apis.getRefreshToken() : refreshing_token)
}

instance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const request = error.config;
    // Should redirect if refreshToken api fails
    if (request?.url === `${process.env.GATSBY_REFRESH_TOKEN_URL}` || request?.url === process.env.GATSBY_ACCESS_TOKEN_URL) {
      refreshTokenErrorRedirect();
      return Promise.reject(error);
    }

    if (error.response && (error.response.status === 401) && !request._retry) {
      request._retry = true;
      try {
        if (localStorage.getItem('myudf_token') || localStorage.getItem('udf_token')) {
          if (localStorage.getItem('udf_token')) {
            setXClientHeader('EMS-ESHOP-EXT')
          }
          refreshing_token = shouldCallRefreshToken();
          const res: any = await refreshing_token;
          refreshing_token = null;
          let access_token = TokenService.getLocalAccessToken();
          let channelToken = TokenService.getUDFChannelToken()
          if (res !== null && Object.keys(res.data).length) {
            if (localStorage.getItem("udf_token")) {
              TokenService.setEmpowerTokenData(res.data);
              access_token = res.data.accessToken;
            } else {
              TokenService.setLocalTokenData(res.data);
              access_token = res.data.tokenB;
            }
          }
          setAuthorizationToken(access_token);
          const channelTokken = await apis.fetchChannelToken();
          channelToken = channelTokken.data.access_token
          TokenService.setUDFChannelToken(channelTokken.data.access_token);
          setApigeeToken(channelToken);
          setxAuthToken(access_token);
          const { store } = createStore;
          setxPageKeyToken(store.getState().auth.check);
          setPromoConfigToken(store.getState().promoConfigs.isPromoConfigured);
          request.headers['Authorization'] = 'Bearer ' + channelToken;
          request.headers['X-Authorization'] = access_token;
          if (store.getState().auth.isStaffLogin) {
            setXClientHeader('EMS-ESHOP-STAFF')
          }
          store.dispatch(actions.getUserGroupPolicies.request())
        } else if (localStorage.getItem('pdpaToken') && TokenService.getPdpaChannelToken()) {
          const { store } = createStore;
          const newDate = new Date();
          const auth = store.getState().auth;
          if (TokenService.getLocalPdpaTokenData().expiryAt < newDate.getTime()) {
            const channelTokken = await apis.pdpaValidate(auth.key);
            window.localStorage.setItem('channelTokenPdpa', JSON.stringify(channelTokken.data));
            const pdpaToken = JSON.parse(localStorage.getItem('pdpaToken') || '{}');
            request.headers['Authorization'] = 'Bearer ' + channelTokken.data.access_token;
            request.headers['X-Authorization'] = pdpaToken.refreshToken;
            setApigeeToken(channelTokken.data.access_token);
            setxAuthToken(pdpaToken.refreshToken);
            const pdpaAccessToken = await apis.pdpaRefreshToken(auth.key);
            localStorage.setItem('pdpaToken', JSON.stringify(pdpaAccessToken.data));
            request.headers['Authorization'] = 'Bearer ' + channelTokken.data.access_token;
            request.headers['X-Authorization'] = pdpaAccessToken.data.accessToken;
            request.headers['X-CLIENT-ID'] = 'EMS-ESHOP-PDPA';
            setApigeeToken(channelTokken.data.access_token);
            setxAuthToken(pdpaAccessToken.data.accessToken);
            setXClientHeader('EMS-ESHOP-PDPA');
            store.dispatch(actions.getUserGroupPolicies.request())
          } else {
            const pdpaToken = JSON.parse(localStorage.getItem('pdpaToken') || '{}');
            const pdpaChannelToken = JSON.parse(localStorage.getItem('channelTokenPdpa') || '{}');
            request.headers['Authorization'] = 'Bearer ' + pdpaChannelToken.access_token;
            request.headers['X-Authorization'] = pdpaToken.accessToken;
            request.headers['X-CLIENT-ID'] = 'EMS-ESHOP-PDPA';
            setApigeeToken(pdpaChannelToken.access_token);
            setxAuthToken(pdpaToken.accessToken);
            setXClientHeader('EMS-ESHOP-PDPA');
            store.dispatch(actions.getUserGroupPolicies.request())
          }
        }
        return instance(request);
      } catch (error) {
        refreshTokenErrorRedirect();
        return Promise.resolve(error);
      }
    }
    return Promise.reject(error);
  }
);

export default instance;
